/**
 * VenuePinLoginPage.jsx — Kiosk PIN entry.
 *
 * Route: /venue-terminal/:vendorId/login?next=/venue-terminal/...
 *
 * Kiosk-friendly on-screen keypad. Reads the venue's PIN policy so
 * the keypad shows digits only, letters only, or both, and enforces
 * length (4 or 6). Stores the returned session JWT in localStorage
 * plus a session cookie so subsequent /venue-terminal API calls just
 * work. Every "close bill" flow can force-lock back to this page —
 * see `useKioskLock` wired into the Terminal and Kitchen pages.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Delete, LogIn, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const NUMERIC = "1 2 3 4 5 6 7 8 9 0".split(" ");
const ALPHA   = "A B C D E F G H I J K L M N O P Q R S T U V W X Y Z".split(" ");

export const VENUE_PIN_TOKEN_KEY = "venue_pin_token";
export const VENUE_PIN_STAFF_KEY = "venue_pin_staff";
export const VENUE_PIN_EXP_KEY   = "venue_pin_exp";

export default function VenuePinLoginPage() {
  const { vendorId }        = useParams();
  const [sp]                = useSearchParams();
  const nav                 = useNavigate();
  const next                = sp.get("next") || `/venue-terminal/${vendorId}`;

  const [policy, setPolicy] = useState({ format: "numeric", length: 4 });
  const [pin, setPin]       = useState("");
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");

  useEffect(() => {
    document.title = "PIN — HustleMeetups Kiosk";
    // Wipe any stale token so re-login always cleans the slate.
    localStorage.removeItem(VENUE_PIN_TOKEN_KEY);
    localStorage.removeItem(VENUE_PIN_STAFF_KEY);
    localStorage.removeItem(VENUE_PIN_EXP_KEY);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/venue-pin/policy/${vendorId}`);
        setPolicy({ format: data.format || "numeric",
                    length: data.length || 4,
                    session_hours: data.session_hours || 8 });
      } catch { /* keep defaults */ }
    })();
  }, [vendorId]);

  const keys = useMemo(() => {
    if (policy.format === "alpha")        return ALPHA;
    if (policy.format === "alphanumeric") return [...ALPHA, ...NUMERIC];
    return NUMERIC;
  }, [policy.format]);

  const push = (k) => {
    setErr("");
    if (pin.length >= policy.length) return;
    setPin((p) => p + k);
  };
  const back = () => { setErr(""); setPin((p) => p.slice(0, -1)); };
  const clr  = () => { setErr(""); setPin(""); };

  const submit = async (finalPin) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/venue-pin/login/${vendorId}`, { pin: finalPin });
      localStorage.setItem(VENUE_PIN_TOKEN_KEY, data.token);
      localStorage.setItem(VENUE_PIN_STAFF_KEY, JSON.stringify(data.staff));
      localStorage.setItem("token", data.token); // reuse the primary token
      const expMs = Date.now() + (data.session_hours || 8) * 3_600_000;
      localStorage.setItem(VENUE_PIN_EXP_KEY, String(expMs));
      toast.success(`Welcome, ${data.staff?.name || "Staff"}`);
      nav(next, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid PIN";
      setErr(msg);
      setPin("");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (pin.length === policy.length && !busy) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, policy.length]);

  const dots = Array.from({ length: policy.length }, (_, i) =>
    i < pin.length ? "●" : "○");

  return (
    <div
      data-testid="venue-pin-login-page"
      className="min-h-screen bg-gradient-to-br from-slate-950 via-stone-900 to-slate-950 text-stone-100 flex items-center justify-center px-4 py-6"
    >
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <KeyRound size={20} className="text-amber-400"/>
          <span className="text-[10px] uppercase tracking-[0.32em] font-black text-amber-400">Kiosk Sign-in</span>
        </div>
        <h1 className="text-center text-3xl font-black">Enter your PIN</h1>
        <p className="mt-2 text-center text-xs text-stone-400">
          {policy.format === "numeric"      && `${policy.length}-digit PIN`}
          {policy.format === "alpha"        && `${policy.length}-letter PIN`}
          {policy.format === "alphanumeric" && `${policy.length}-character PIN`}
          {" · venue "}<code>{vendorId}</code>
        </p>

        <div
          data-testid="pin-dots"
          className="mt-8 flex justify-center gap-3 text-3xl font-black text-amber-300 tabular-nums"
        >
          {dots.map((d, i) => <span key={i}>{d}</span>)}
        </div>

        {err && (
          <p data-testid="pin-err"
             className="mt-4 text-center text-red-400 text-sm font-black">
            {err}
          </p>
        )}

        <div className={`mt-8 grid gap-3 ${policy.format === "numeric" ? "grid-cols-3" : "grid-cols-4"}`}>
          {keys.map((k) => (
            <button
              key={k}
              data-testid={`pin-key-${k}`}
              onClick={() => push(k)}
              disabled={busy}
              className="rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-2xl font-black py-4"
            >
              {k}
            </button>
          ))}
          <button
            data-testid="pin-clear"
            onClick={clr}
            disabled={busy}
            className="rounded-2xl bg-white/5 hover:bg-red-500/20 border border-white/10 py-4 font-black text-red-300 text-xs uppercase tracking-widest"
          >
            Clear
          </button>
          <button
            data-testid="pin-back"
            onClick={back}
            disabled={busy}
            className="col-span-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 py-4 font-black inline-flex items-center justify-center gap-2"
          >
            <Delete size={18}/> Delete
          </button>
        </div>

        <p className="mt-8 text-center text-[10px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1 w-full justify-center">
          <Lock size={11}/> Kiosk mode · PIN required for every transaction
        </p>
      </div>
    </div>
  );
}
